// src/components/ui/BottomNav.tsx — fixed bottom tab bar.
//
// Port of docs/prototype/shell.jsx 76-122 with the audit-resolved drift fixes
// from STATUS §3:
//   - Background C.paperSoft (was C.paper)
//   - borderTop colour C.hairlineLt (was C.hairline)
//   - Active icon pill 42 wide x 36 tall (prototype had 30; audit set 36)
//   - Label colour active C.brassDk weight 800; inactive C.inkMute weight 700
//   - Label font: body, 12px, capitalised "Home/Apps/Docs/Me" (NOT mono uppercase)
//
// MobileFrame derives `active` from useLocation() and supplies onChange via
// navigate(); this primitive is purely presentational with no react-router
// dependency.

import type { CSSProperties, ReactElement } from "react";
import { C, F, layout } from "../../theme";
import * as Icons from "./Icon";
import type { IconProps } from "./Icon";

export type BottomTabId = "home" | "apps" | "docs" | "me";

export interface BottomNavProps {
  active: BottomTabId;
  onChange: (id: BottomTabId) => void;
  /** Per-tab unread indicator. A non-empty truthy value renders a small
   * coloured dot on the tab. Phase 7 uses this to surface Quick Message
   * unread on the Home tab in QM-blue (#3D6FA8 -- reserved for QM only). */
  dots?: Partial<Record<BottomTabId, { color: string } | false>>;
}

type IconComponent = (props: IconProps) => ReactElement;

interface NavEntry {
  id: BottomTabId;
  label: string;
  Icon: IconComponent;
}

const NAV: ReadonlyArray<NavEntry> = [
  { id: "home", label: "Home", Icon: Icons.Home },
  { id: "apps", label: "Apps", Icon: Icons.Grid },
  { id: "docs", label: "Docs", Icon: Icons.Doc  },
  { id: "me",   label: "Me",   Icon: Icons.User },
];

const navStyle: CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 60,
  background: C.paperSoft,
  borderTop: `1px solid ${C.hairlineLt}`,
  minHeight: layout.bottomNavH,
  paddingTop: 6,
  paddingBottom: "max(8px, env(safe-area-inset-bottom))",
  paddingLeft: "max(8px, env(safe-area-inset-left))",
  paddingRight: "max(8px, env(safe-area-inset-right))",
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: 4,
  boxShadow: "0 -8px 22px rgba(122,84,11,0.06)",
};

export const BottomNav = ({ active, onChange, dots }: BottomNavProps) => (
  <nav style={navStyle}>
    {NAV.map(({ id, label, Icon }) => {
      const on = active === id;
      const dot = dots?.[id] || undefined;
      return (
        <button
          key={id}
          type="button"
          onClick={() => onChange(id)}
          aria-current={on ? "page" : undefined}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
            padding: "4px 4px",
            color: on ? C.brassDk : C.inkMute,
            fontFamily: F.body,
            fontWeight: on ? 800 : 700,
            fontSize: 12,
            letterSpacing: 0.3,
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <div
            style={{
              position: "relative",
              width: 42,
              height: 36,
              borderRadius: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: on ? C.brassBg : "transparent",
              border: on ? `1px solid ${C.brassLt}` : "1px solid transparent",
              transition: "all 0.15s",
            }}
          >
            <Icon s={22} c={on ? C.brassDk : C.inkMute} />
            {dot && (
              <span
                aria-label="Unread"
                style={{
                  position: "absolute",
                  top: 4,
                  right: 8,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: dot.color,
                  border: `1.5px solid ${C.paperSoft}`,
                }}
              />
            )}
          </div>
          {label}
        </button>
      );
    })}
  </nav>
);
